import { Button, ManageableItemsList, Modal } from '@payaca/ambrosia';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useSelector } from '@/api/state';
import { getAutomationFilterEntityTypesLocalStorageKey } from '@/helpers/localStorageKeyHelper';
import { getUserRoles } from '@/utils/stateAccessors';

import {
  automationActionTypeReadableNameMap,
  automationEntityTypeReadableNameMap,
} from '@payaca/helpers/automationHelper';
import { AutomationsPermissions } from '@payaca/permissions/automations/automations.permissions';
import { userHasRequiredPermission } from '@payaca/permissions/permissions.utils';
import {
  requestArchiveAutomations,
  requestCreateAutomation,
  requestGetListedAutomationsPage,
} from '@payaca/store/automations/automationActions';
import {
  automationEntityTypes,
  automationTriggerEvents,
  GetListedAutomationsRequestData,
  ListedAutomation,
  ListedAutomationsListViewPage,
  SortBy,
} from '@payaca/types/automationTypes';
import { SortDirection } from '@payaca/types/listViewTypes';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import AutomationsTable from '../automationsTable/AutomationsTable';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';
import './CustomAutomations.sass';

const CustomAutomations: FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const userRoles = useSelector(getUserRoles);

  const [automationsPage, setAutomationsPage] =
    useState<ListedAutomationsListViewPage>();

  const [isArchivingAutomation, setIsArchivingAutomation] = useState(false);
  const [isGettingListedAutomationsPage, setIsGettingListedAutomationsPage] =
    useState(false);

  const onClickRow = useCallback(
    (automationId: number) => history.push(`/automations/${automationId}`),
    [history]
  );

  const [
    showConfirmDeleteAutomationsModal,
    setShowConfirmDeleteAutomationsModal,
  ] = useState(false);
  const [selectedAutomationId, setSelectedAutomationId] =
    useState<ListedAutomation['id']>();

  const [getListedAutomationsRequestData, setGetListedAutomationsRequestData] =
    useState<GetListedAutomationsRequestData>(() => {
      const locallyStoredRequestData = localStorage.getItem(
        getAutomationFilterEntityTypesLocalStorageKey()
      );

      return {
        sortBy: SortBy.UPDATED_AT,
        sortDirection: SortDirection.DESCENDING,
        pageSize: 20,
        pageNumber: 1,
        searchTerm: '',
        ...JSON.parse(locallyStoredRequestData || '{}'),
      };
    });

  useEffect(() => {
    getListedAutomationsPage();
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'getListedAutomationsPage'
  }, [getListedAutomationsRequestData]);

  const getListedAutomationsPage = useCallback(() => {
    setIsGettingListedAutomationsPage(true);
    dispatch(
      requestGetListedAutomationsPage(
        getListedAutomationsRequestData,
        (page) => {
          setAutomationsPage(page);
          setIsGettingListedAutomationsPage(false);
        }
      )
    );
  }, [getListedAutomationsRequestData, dispatch]);

  const setGetListedAutomationsRequestDataUsingLocalStorage = (
    stateChange: (
      prevData: GetListedAutomationsRequestData
    ) => GetListedAutomationsRequestData
  ) => {
    setGetListedAutomationsRequestData((prevData) => {
      const newData = stateChange(prevData);

      localStorage.setItem(
        getAutomationFilterEntityTypesLocalStorageKey(),
        JSON.stringify(newData)
      );

      return newData;
    });
  };

  const onSort = (field: string, sortDirection: SortDirection) => {
    setGetListedAutomationsRequestDataUsingLocalStorage((prevData) => ({
      ...prevData,
      sortBy: field as SortBy,
      sortDirection: sortDirection,
      pageNumber: 1,
    }));
  };

  const onChangeFilters = (filterKey: string) => (value: string[]) => {
    setGetListedAutomationsRequestDataUsingLocalStorage((prevData) => ({
      ...prevData,
      [filterKey]: value,
      pageNumber: 1,
    }));
  };

  const quickActionDefinitions = useMemo(() => {
    return userHasRequiredPermission(userRoles, [
      AutomationsPermissions.DELETE_AUTOMATION,
    ])
      ? [
          {
            actionName: 'Delete',
            actionBehaviour: (automationId: number) => {
              setSelectedAutomationId(automationId);
              setShowConfirmDeleteAutomationsModal(true);
            },
          },
        ]
      : [];
  }, [userRoles]);

  const onDeleteAutomation = useCallback(
    (automationId: ListedAutomation['id']) => {
      setIsArchivingAutomation(true);
      dispatch(
        requestArchiveAutomations([automationId], () => {
          setIsArchivingAutomation(false);
          setShowConfirmDeleteAutomationsModal(false);
          setSelectedAutomationId(undefined);
          getListedAutomationsPage();
        })
      );
    },
    [dispatch, getListedAutomationsPage]
  );

  const entityTypeOptions = automationEntityTypes
    .filter((x) => !['Invoice-v2'].includes(x))
    .map((value) => ({
      label: automationEntityTypeReadableNameMap[value] || value,
      value: value,
    }));

  const triggerEventOptions = automationTriggerEvents.map((label: string) => {
    return {
      label,
      value: label,
    };
  });

  const actionTypeOptions = (
    [
      'E-mail Notification',
      'SMS Notification',
      'Create repeat Project',
    ] as const
  ).map((value) => {
    return {
      label: automationActionTypeReadableNameMap[value] || value,
      value: value,
    };
  });

  return (
    <>
      <ManageableItemsList>
        <ManageableItemsList.HeaderBar
          heading="Custom Automations"
          buttons={
            <PermissionGuard
              renderIfHasPermissions={[AutomationsPermissions.ADD_AUTOMATION]}
            >
              <Button
                size="sm"
                onClick={() => {
                  dispatch(
                    requestCreateAutomation({}, (automationId: number) => {
                      history.push(`/automations/${automationId}`);
                    })
                  );
                }}
              >
                Create custom automation
              </Button>
            </PermissionGuard>
          }
        />

        <ManageableItemsList.ActionBar>
          <ManageableItemsList.ActionBar.SearchInput
            onChangeTimeout={(searchTerm) => {
              setGetListedAutomationsRequestData((x) => ({
                ...x,
                searchTerm: searchTerm,
                pageNumber: 1,
              }));
            }}
          />

          <ManageableItemsList.ActionBar.BasicFilter
            name="Type"
            onChange={onChangeFilters('entityTypes')}
            value={getListedAutomationsRequestData.entityTypes || []}
            options={entityTypeOptions}
          />

          <ManageableItemsList.ActionBar.BasicFilter
            name="Trigger event"
            onChange={onChangeFilters('triggerEvents')}
            value={getListedAutomationsRequestData.triggerEvents || []}
            options={triggerEventOptions}
          />

          <ManageableItemsList.ActionBar.BasicFilter
            name="Action types"
            onChange={onChangeFilters('actionTypes')}
            value={getListedAutomationsRequestData.actionTypes || []}
            options={actionTypeOptions}
          />
        </ManageableItemsList.ActionBar>

        <AutomationsTable
          automations={automationsPage?.items || []}
          onClickRow={onClickRow}
          quickActionDefinitions={quickActionDefinitions}
          isLoading={isGettingListedAutomationsPage}
          onSort={onSort}
          sortBy={getListedAutomationsRequestData.sortBy}
          sortDirection={getListedAutomationsRequestData.sortDirection}
        />

        <ManageableItemsList.PaginationBar
          type="custom"
          currentPage={automationsPage?.pageNumber || 1}
          pageSize={automationsPage?.pageSize || 0}
          totalItems={automationsPage?.totalItemCount || 0}
          onPageChange={(pageNumber) => {
            setGetListedAutomationsRequestData((x) => ({
              ...x,
              pageNumber: pageNumber,
            }));
          }}
        />
      </ManageableItemsList>

      <Modal
        isOpen={showConfirmDeleteAutomationsModal}
        onClose={() => {
          setShowConfirmDeleteAutomationsModal(false);
        }}
        title="Delete Automation"
      >
        <Modal.Body>Are you sure you want to delete 1 Automation?</Modal.Body>

        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button
              variant="outline"
              onClick={() => {
                setShowConfirmDeleteAutomationsModal(false);
              }}
            >
              Cancel
            </Button>
            <Button
              colour="red"
              onClick={() => {
                if (isArchivingAutomation || !selectedAutomationId) return;

                onDeleteAutomation(selectedAutomationId);
              }}
              isProcessing={isArchivingAutomation}
            >
              Delete
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CustomAutomations;
